import { useState, ReactNode } from "react";
import { Select, Divider, SelectProps } from "antd";
import styled, { css } from "styled-components";
import { Text } from "../../general";
import Chevron from "../../systemIcons/Chevron/Chevron";
import { BaseOptionType, DefaultOptionType } from "antd/es/select";

export type Option<ValueType = string> = {
  label: string;
  value: ValueType;
  image?: ReactNode;
};

export type GroupedOption<OptionType> = {
  label: string;
  options: OptionType[];
};

export type SelectVariant = "default" | "highContrast";

/**
 * MultiSelect accepts options or groupedOptions
 */
export type MultiSelectProps<
  ValueType = string,
  OptionType extends Option<ValueType> = Option<ValueType>,
  GroupedOptionType extends
    GroupedOption<OptionType> = GroupedOption<OptionType>,
> = {
  title: string;
  options?: OptionType[];
  groupedOptions?: GroupedOptionType[];
  onChange?: (values: ValueType[]) => void;
  values?: ValueType[];
  hasSearch?: boolean;
  hasClear?: boolean;
  hasSelectAll?: boolean;
  className?: string;
  placeholder?: boolean;
  capitalizedLabel?: boolean;
  variant?: SelectVariant;
  maxTagCount?: number | null;
  suffixIcon?: ReactNode;
  "data-testid"?: string;
};

const filterFunction = (input: string, option: Option | undefined) => {
  if (option) {
    const label = option.label.toString().toLowerCase();
    return label.includes(input.toLowerCase());
  }
  return false;
};

const MultiSelect = <
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  ValueType extends any = any,
  OptionType extends Option = Option,
>({
  title,
  options,
  groupedOptions,
  onChange,
  values = [],
  className,
  hasSearch = false,
  hasClear = false,
  hasSelectAll = false,
  placeholder = true,
  capitalizedLabel = true,
  variant = "default",
  maxTagCount = 0,
  suffixIcon = <Chevron direction="down" />,
  "data-testid": dataTestId,
}: MultiSelectProps<ValueType, Option<ValueType>>) => {
  const [selectedValues, setSelectedValues] = useState(values);

  const handleChange = (selectedOptions: ValueType[]) => {
    setSelectedValues(selectedOptions);
    if (onChange) {
      onChange(selectedOptions);
    }
  };

  const groupedOptionIds = groupedOptions?.map((option) =>
    option?.options?.map((option) => option.value),
  )[0];

  return (
    <SelectStyled
      $variant={variant}
      className={`${className} select_wrapper`}
      mode="multiple"
      allowClear
      onClear={() => handleChange([])}
      showSearch={hasSearch}
      filterOption={filterFunction}
      onChange={handleChange}
      value={selectedValues}
      maxTagCount={typeof maxTagCount === "number" ? maxTagCount : undefined}
      suffixIcon={suffixIcon}
      data-testid={dataTestId}
      placeholder={
        placeholder && (
          <StyledPlaceholder level={1} $variant={variant}>
            {title}
          </StyledPlaceholder>
        )
      }
      maxTagPlaceholder={
        <Text level={1}>
          {title} ({selectedValues?.length})
        </Text>
      }
      dropdownRender={(menuOptions) => (
        <>
          {menuOptions}
          {(hasClear || hasSelectAll) && (
            <>
              <StyledDivider />
              <ButtonsContainer>
                <StyledButton onClick={() => handleChange([])}>
                  <Text level={1}>Clear</Text>
                </StyledButton>
                {options && (
                  <StyledButton
                    onClick={() => {
                      handleChange(options.map((option) => option.value));
                    }}
                  >
                    <Text level={1}>Select All</Text>
                  </StyledButton>
                )}
                {groupedOptionIds && (
                  <StyledButton
                    onClick={() => {
                      handleChange(groupedOptionIds);
                    }}
                  >
                    <Text level={1}>Select All</Text>
                  </StyledButton>
                )}
              </ButtonsContainer>
            </>
          )}
        </>
      )}
    >
      {options &&
        options?.map((option) => (
          <Select.Option
            key={String(option.value)}
            value={option.value}
            label={option.label}
          >
            <OptionContainer>
              <ImageContainer>{option.image}</ImageContainer>
              <StyledText isCapitalized={capitalizedLabel}>
                {option.label}
              </StyledText>
            </OptionContainer>
          </Select.Option>
        ))}
      {groupedOptions &&
        groupedOptions.map((group) => (
          <Select.OptGroup key={group.label} label={group.label}>
            {group.options.map((option) => (
              <Select.Option
                key={String(option.value)}
                value={option.value}
                label={option.label}
              >
                <OptionContainer>
                  <ImageContainer>{option.image} </ImageContainer>
                  <StyledText>{option.label}</StyledText>
                </OptionContainer>
              </Select.Option>
            ))}
          </Select.OptGroup>
        ))}
    </SelectStyled>
  );
};

interface StyledSelectProps<V, O extends BaseOptionType | DefaultOptionType>
  extends SelectProps<V, O> {
  $variant?: string;
}

const SelectStyled = styled(Select)<StyledSelectProps<any, Option>>`
  min-width: 300px;

  ${({ $variant, theme }) =>
    $variant === "highContrast" &&
    css`
      .ant-select-selector {
        border-color: ${theme.color.neutral.color5} !important;
      }

      .ant-select-arrow {
        color: ${theme.color.neutral.color5} !important;
      }
    `}
`;

const StyledButton = styled.button`
  all: unset;
  cursor: pointer;
  color: ${(props) => props.theme.color.status.primary};
  :hover {
    opacity: 0.7;
  }
`;

const ButtonsContainer = styled.div`
  padding: 8px 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: normal;
  word-break: break-word;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledText = styled(Text)<{ isCapitalized?: boolean }>`
  text-transform: ${(props) => (props.isCapitalized ? "capitalized" : "none")};
  color: ${(props) => props.theme.color.neutral.color3};
`;

const StyledDivider = styled(Divider)`
  margin: 4px 0;
`;

const StyledPlaceholder = styled(Text)<{ $variant: string }>`
  color: ${(props) =>
    props.$variant === "highContrast"
      ? props.theme.color.neutral.color3
      : "inherit"};
`;

export default MultiSelect;
